import React, { useState } from "react";
import { Formik, Form } from "formik";
import PropTypes from "prop-types";
import CustomInputForm from "components/CustomInput/CustomInputForm";
import CustomSelect from "components/CustomSelect/CustomSelect";
import Grid from "@material-ui/core/Grid";
import CustomRadio from "components/CustomRadio/CustomRadio";
import FormLabel from "@material-ui/core/FormLabel";
import CustomDataPicker from "components/CustomDataPicker/CustomDataPicker";
import FormControl from "@material-ui/core/FormControl";
import CardForms from "../../components/Card/CardForms";
import AmortizationTable from "components/AmortizationTable/AmortizationTable";
import useFormStyles from "../../styles/useFormStyles";
import Button from "components/CustomButtons/Button";
import Typography from "@material-ui/core/Typography";
import MenuItem from "@material-ui/core/MenuItem";
import Divider from "@material-ui/core/Divider";
import Tooltip from "@material-ui/core/Tooltip";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import validationSchema from "schema/schemaFormContract";

const fmtMoney = val =>
  "$" + parseFloat(val || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 });

const FormContract = ({
  inputcosto,
  infoclientes,
  infolotes,
  infodireccioncliente,
  handlePayment,
  generatePdf,
  paymentMade
}) => {
  const classes = useFormStyles();
  const [interesCustom, setInteresCustom] = useState(false);

  const calcMensualidad = (costo, enganche, meses) => {
    if (!meses || meses <= 0) return 0;
    const saldo = parseFloat(costo || 0) - parseFloat(enganche || 0);
    return Math.floor(saldo / meses);
  };

  return (
    <CardForms title={"Generación de contrato"}>
      <Formik
        initialValues={{
          tipocontrato: "contado",
          testigo1: "",
          testigo2: "",
          mensualidades: "",
          intereses: 0,
          enganche: "",
          costo: inputcosto,
          fechapago: ""
        }}
        validationSchema={validationSchema}
        onSubmit={async values => {
          if (!paymentMade) {
            if (!window.confirm("¿Está seguro de generar el contrato?")) return;
            await handlePayment(values, infoclientes, infolotes);
          } else {
            const pdfUrl = await generatePdf(
              values, infoclientes, infolotes, infodireccioncliente
            );
            window.open(pdfUrl, "_blank");
          }
        }}
      >
        {({ values, errors, touched, setFieldValue }) => {
          // Si seleccionaron "Otro..." en el select de interés
          if (values.intereses === -1 && !interesCustom) {
            setInteresCustom(true);
            setTimeout(() => setFieldValue("intereses", ""), 0);
          }
          return (
          <Form>
            {/* TIPO DE CONTRATO */}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl
                  component="fieldset"
                  className={classes.formControlFlex}
                  disabled={paymentMade}
                >
                  <FormLabel component="legend" style={{ marginBottom: 4 }}>
                    Tipo de contrato
                  </FormLabel>
                  <CustomRadio label="Contado" name="tipocontrato" value="contado" disabled={paymentMade} />
                  <CustomRadio label="Crédito" name="tipocontrato" value="credito" disabled={paymentMade} />
                </FormControl>
              </Grid>
            </Grid>

            {/* SECCIÓN CRÉDITO */}
            {values.tipocontrato === "credito" && (
              <>
                <Divider style={{ margin: "16px 0 20px" }} />
                <Typography variant="subtitle2" style={{ color: "#1565c0", fontWeight: 600, marginBottom: 12 }}>
                  Condiciones del crédito
                </Typography>
                <Grid container spacing={3}>
                  {/* Enganche */}
                  <Grid item xs={12} sm={6}>
                    <CustomInputForm
                      labelText="Monto del enganche"
                      name="enganche"
                      type="number"
                      disabled={paymentMade}
                      formcontrolprops={{ fullWidth: true }}
                      error={Boolean(touched.enganche && errors.enganche)}
                    />
                  </Grid>

                  {/* Mensualidades */}
                  <Grid item xs={12} sm={6}>
                    <CustomInputForm
                      labelText="Número de mensualidades"
                      name="mensualidades"
                      type="number"
                      disabled={paymentMade}
                      formcontrolprops={{ fullWidth: true }}
                      error={Boolean(touched.mensualidades && errors.mensualidades)}
                    />
                  </Grid>

                  {/* Interés */}
                  <Grid item xs={12} sm={6}>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                      {!interesCustom ? (
                        <CustomSelect
                          label="Tasa de interés moratorio"
                          name="intereses"
                          disabled={paymentMade}
                        >
                          <MenuItem value={0}>Sin interés</MenuItem>
                          <MenuItem value={5}>5%</MenuItem>
                          <MenuItem value={10}>10%</MenuItem>
                          <MenuItem value={15}>15%</MenuItem>
                          <MenuItem value={20}>20%</MenuItem>
                          <MenuItem value={-1}>Otro...</MenuItem>
                        </CustomSelect>
                      ) : (
                        <CustomInputForm
                          labelText="Tasa de interés moratorio (%)"
                          name="intereses"
                          type="number"
                          disabled={paymentMade}
                          formcontrolprops={{ fullWidth: true }}
                          error={Boolean(touched.intereses && errors.intereses)}
                        />
                      )}
                    </div>
                    <Typography variant="caption" style={{ color: "#888", marginTop: 4, display: "block" }}>
                      <Tooltip title="El interés moratorio se aplica solo cuando una mensualidad entra en mora. Máximo 30% por políticas de la empresa.">
                        <span style={{ cursor: "help", display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <InfoOutlinedIcon style={{ fontSize: 14 }} />
                          Solo aplica a parcialidades con mora
                        </span>
                      </Tooltip>
                    </Typography>
                  </Grid>

                  {/* Monto mensualidad (calculado) */}
                  <Grid item xs={12} sm={6}>
                    <CustomInputForm
                      disabled
                      labelText="Monto por mensualidad"
                      name="pagos"
                      formcontrolprops={{ fullWidth: true }}
                      value={
                        values.mensualidades > 0
                          ? fmtMoney(calcMensualidad(inputcosto, values.enganche, values.mensualidades))
                          : "$ 0.00"
                      }
                    />
                    {values.mensualidades > 0 && (
                      <Typography variant="caption" style={{ color: "#888" }}>
                        Última mensualidad puede variar para cubrir el residuo
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </>
            )}

            {/* MONTO CONTADO */}
            {values.tipocontrato === "contado" && (
              <Grid container spacing={3} style={{ marginTop: 8 }}>
                <Grid item xs={12} sm={6}>
                  <CustomInputForm
                    disabled
                    labelText="Monto total de compra"
                    name="pagos"
                    formcontrolprops={{ fullWidth: true }}
                    value={fmtMoney(inputcosto)}
                  />
                </Grid>
              </Grid>
            )}

            {/* TESTIGOS Y FECHA */}
            <Divider style={{ margin: "20px 0" }} />
            <Typography variant="subtitle2" style={{ color: "#1565c0", fontWeight: 600, marginBottom: 12 }}>
              Datos adicionales
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <CustomInputForm
                  labelText="Testigo 1 (nombre completo)"
                  name="testigo1"
                  disabled={paymentMade}
                  formcontrolprops={{ fullWidth: true }}
                  error={Boolean(touched.testigo1 && errors.testigo1)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomInputForm
                  labelText="Testigo 2 (nombre completo)"
                  name="testigo2"
                  disabled={paymentMade}
                  formcontrolprops={{ fullWidth: true }}
                  error={Boolean(touched.testigo2 && errors.testigo2)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomDataPicker
                  name="fechapago"
                  disabled={paymentMade}
                  label={
                    values.tipocontrato === "credito"
                      ? "Fecha de primer pago"
                      : "Fecha de generación de contrato"
                  }
                  InputLabelProps={{ shrink: true }}
                  maxDate={
                    new Date(new Date().getFullYear(), new Date().getMonth() + 2, 0)
                  }
                  error={Boolean(touched.fechapago && errors.fechapago)}
                />
              </Grid>
            </Grid>

            {/* TABLA AMORTIZACIÓN */}
            {values.tipocontrato === "credito" && values.mensualidades > 0 && (
              <Grid container spacing={3} style={{ marginTop: 16 }}>
                <Grid item xs={12}>
                  <AmortizationTable
                    loanAmount={inputcosto}
                    interestRate={parseFloat(values.intereses) || 0}
                    loanTerm={parseFloat(values.mensualidades)}
                    creditDownPayment={parseFloat(values.enganche) || 0}
                    paymentDay={values.fechapago}
                  />
                </Grid>
              </Grid>
            )}

            {/* BOTONES */}
            <Grid container spacing={3} style={{ marginTop: 16 }}>
              <Grid item xs={6}>
                <Button type="submit" color="success" disabled={paymentMade}>
                  Generar Contrato
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button type="submit" color="info" disabled={!paymentMade}>
                  Descargar PDF
                </Button>
              </Grid>
            </Grid>

            {paymentMade && (
              <Grid container style={{ marginTop: 12 }}>
                <Grid item xs={12}>
                  <Typography variant="body2" style={{
                    color: "#e65100", backgroundColor: "#fff3e0",
                    padding: "8px 12px", borderRadius: 6, border: "1px solid #ffcc80"
                  }}>
                    Contrato generado. Para activarlo, el cajero debe cobrar el enganche desde la pantalla de Cobranza.
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Form>
        );
        }}
      </Formik>
    </CardForms>
  );
};

FormContract.propTypes = {
  inputcosto: PropTypes.number,
  infoclientes: PropTypes.object,
  infolotes: PropTypes.object,
  infodireccioncliente: PropTypes.array,
  handlePayment: PropTypes.func,
  generatePdf: PropTypes.func,
  paymentMade: PropTypes.bool
};

export default FormContract;
